import React, { createContext, useContext, useEffect, useState } from 'react';

// Static UI translations for menu/buttons/headings. Exercise content is translated via backend LLM.
const translations = {
  en: {
    home: 'Home',
    exercises: 'Exercises',
    favorites: 'Favorites',
    proPack: 'Pro Pack',
    backToHome: 'Back to Home',
    language: 'Language',

    // Home page
    badge: 'Ages 9-12 • Little League Players',
    heroLine1: 'Become a Better',
    heroLine2: 'Baseball Player',
    heroSubtitle: "Master essential baseball skills with fun, engaging exercises designed specifically for young athletes. From batting to pitching, we've got the drills to help you excel!",
    exploreExercises: 'Explore Exercises',
    watchDemo: 'Watch Demo',
    whyOurTraining: 'Why Our Training Works',
    designedByCoaches: 'Designed by coaches who understand youth baseball',
    popularExercises: 'Popular Exercises',
    startWithThese: 'Start with these fan-favorite drills',
    viewExercise: 'View Exercise',
    viewAllExercises: 'View All Exercises',
    readyToLevelUp: 'Ready to Level Up Your Game?',
    joinThousands: 'Join thousands of young players improving their skills every day with our expert-designed exercises.',
    startTrainingNow: 'Start Training Now',

    // Library
    exerciseLibrary: 'Exercise Library',
    browseAll: 'Browse our complete collection of baseball training exercises',
    filterExercises: 'Filter Exercises',
    findPerfect: 'Find the perfect drill for your training session',
    searchPlaceholder: 'Search exercises by name or description...',
    skillCategory: 'Skill Category',
    difficultyLevel: 'Difficulty Level',
    resetFilters: 'Reset Filters',
    showing: 'Showing',
    exercise: 'exercise',
    exercisesPlural: 'exercises',
    loading: 'Loading exercises...',
    noExercises: 'No exercises found',
    tryAdjusting: 'Try adjusting your filters or search query',
    viewDetails: 'View Details',
    duration: 'Duration',
    equipment: 'Equipment',

    // Detail
    back: 'Back',
    downloadDrillCard: 'Download Drill Card',
    favorite: 'Favorite',
    favorited: 'Favorited',
    videoDemo: 'Video Demonstration',
    stepByStep: 'Step-by-Step Instructions',
    done: 'done',
    proTips: 'Pro Tips',
    equipmentNeeded: 'Equipment Needed',
    quickInfo: 'Quick Info',
    skillFocus: 'Skill Focus',
    difficulty: 'Difficulty',
    moreExercises: 'More',
    exerciseComplete: '🏆 Exercise Complete!',
    keepPracticing: 'Great work! Keep practicing to master this skill.',
    shopAmazon: '🛒 Shop on Amazon →',
    affiliateDisclosure: 'As an Amazon Associate we earn from qualifying purchases.',
    translationFallback: 'Translation unavailable, showing English content.',

    // Pricing
    pricingTitle: 'Simple,',
    pricingTitleEm: 'One-Time',
    pricingTitleEnd: 'Pricing',
    pricingSubtitle: 'No subscriptions. Pay once, own forever.',
    unlockPremium: 'Unlock Premium Drills',
    free: 'Free',
    perfectStart: 'Perfect for getting started',
    forever: 'forever',
    currentPlan: 'Current Plan',
    proPackTitle: 'Pro Pack',
    forCoaches: 'For coaches & dedicated players',
    oneTime: 'one-time',
    bestValue: 'BEST VALUE',
    saveMore: 'SAVE $5',
    bundlePack: 'Bundle Pack',
    forSeriousTeams: 'For serious teams & power users',
    unlockProPack: 'Unlock Pro Pack',
    unlockBundle: 'Unlock Bundle',
    proUnlocked: 'Pro Pack Unlocked',
    bundleUnlocked: 'Bundle Unlocked',
    whatsIn: "What's in the Pro Pack?",
    printReady: 'Print-Ready PDFs',
    printReadyDesc: 'Hand out drill cards to your team or take them to the field',
    offlineAccess: 'Offline Access',
    offlineDesc: 'Download once, use anywhere — no internet needed at practice',
    allDrills: 'All 38 Drills',
    allDrillsDesc: 'Every batting, pitching, fielding, base running drill included',

    // Favorites
    yourFavorites: 'Your Favorites',
    savedForLater: "Exercises you've saved for later",
    noFavoritesYet: 'No favorites yet',
    tapHeart: 'Tap the heart icon on any exercise to save it here.',
    browseExercises: 'Browse Exercises',

    // Payment
    verifyingPayment: 'Verifying Payment...',
    pleaseHold: 'Please hold on while we confirm your purchase.',
    proPackUnlocked: '🎉 Pro Pack Unlocked!',
    canDownload: 'You can now download PDF drill cards for all 38 exercises.',
    paymentCancelled: 'Payment Cancelled',
    noWorries: 'No worries! You can still use all free exercises.',
    tryAgain: 'Try Again',
    sessionExpired: 'Session Expired',
    expiredMsg: 'Your checkout session has expired. Please try again.',
    downloadAllZip: 'Download All as ZIP',

    // Tier names + gating
    basicPlan: 'Basic',
    proPlan: 'Pro',
    ultimatePlan: 'Ultimate',
    basicTagline: '15 essential drills to get started',
    proTagline: '25 drills + downloadable PDFs',
    ultimateTagline: 'All 38 drills + ZIP download',
    locked: 'Locked',
    upgradeToUnlock: 'Upgrade to unlock',
    upgradeToPro: 'Upgrade to Pro',
    upgradeToUltimate: 'Upgrade to Ultimate',
    exerciseLockedTitle: 'This drill is locked',
    exerciseLockedMsg: 'Upgrade your plan to unlock this exercise and more advanced training.',
    viewPlans: 'View Plans',
    yourCurrentPlan: 'Your current plan',
    save5: 'Save $5',
  },
  fr: {
    home: 'Accueil',
    exercises: 'Exercices',
    favorites: 'Favoris',
    proPack: 'Pack Pro',
    backToHome: "Retour à l'accueil",
    language: 'Langue',

    badge: 'Âge 9-12 • Joueurs de Little League',
    heroLine1: 'Devenez un meilleur',
    heroLine2: 'Joueur de Baseball',
    heroSubtitle: "Maîtrisez les compétences essentielles du baseball avec des exercices amusants et engageants conçus spécialement pour les jeunes athlètes. Du frappeur au lanceur, nous avons les drills pour vous aider à exceller !",
    exploreExercises: 'Explorer les Exercices',
    watchDemo: 'Voir la Démo',
    whyOurTraining: 'Pourquoi Notre Formation Fonctionne',
    designedByCoaches: 'Conçu par des entraîneurs qui comprennent le baseball jeunesse',
    popularExercises: 'Exercices Populaires',
    startWithThese: 'Commencez par ces drills favoris des fans',
    viewExercise: "Voir l'Exercice",
    viewAllExercises: 'Voir Tous les Exercices',
    readyToLevelUp: 'Prêt à passer au niveau supérieur ?',
    joinThousands: 'Rejoignez des milliers de jeunes joueurs qui améliorent leurs compétences chaque jour.',
    startTrainingNow: 'Commencer Maintenant',

    exerciseLibrary: 'Bibliothèque des Exercices',
    browseAll: 'Parcourez notre collection complète d\'exercices de baseball',
    filterExercises: 'Filtrer les Exercices',
    findPerfect: 'Trouvez le drill parfait pour votre séance',
    searchPlaceholder: 'Rechercher par nom ou description...',
    skillCategory: 'Catégorie de Compétence',
    difficultyLevel: 'Niveau de Difficulté',
    resetFilters: 'Réinitialiser',
    showing: 'Affichage de',
    exercise: 'exercice',
    exercisesPlural: 'exercices',
    loading: 'Chargement des exercices...',
    noExercises: 'Aucun exercice trouvé',
    tryAdjusting: 'Essayez de modifier vos filtres ou votre recherche',
    viewDetails: 'Voir les Détails',
    duration: 'Durée',
    equipment: 'Équipement',

    back: 'Retour',
    downloadDrillCard: 'Télécharger la Fiche',
    favorite: 'Favori',
    favorited: 'En Favori',
    videoDemo: 'Démonstration Vidéo',
    stepByStep: 'Instructions Étape par Étape',
    done: 'terminé',
    proTips: 'Conseils de Pro',
    equipmentNeeded: 'Équipement Nécessaire',
    quickInfo: 'Infos Rapides',
    skillFocus: 'Compétence',
    difficulty: 'Difficulté',
    moreExercises: 'Plus de',
    exerciseComplete: '🏆 Exercice Terminé !',
    keepPracticing: 'Excellent travail ! Continuez à pratiquer pour maîtriser.',
    shopAmazon: '🛒 Acheter sur Amazon →',
    affiliateDisclosure: 'En tant que membre Amazon Associate, nous gagnons sur les achats éligibles.',
    translationFallback: 'Traduction indisponible, contenu en anglais affiché.',

    pricingTitle: 'Tarification',
    pricingTitleEm: 'Unique',
    pricingTitleEnd: 'Simple',
    pricingSubtitle: 'Pas d\'abonnement. Payez une fois, à vous pour toujours.',
    unlockPremium: 'Débloquer les Drills Premium',
    free: 'Gratuit',
    perfectStart: 'Parfait pour démarrer',
    forever: 'pour toujours',
    currentPlan: 'Plan Actuel',
    proPackTitle: 'Pack Pro',
    forCoaches: 'Pour entraîneurs et joueurs dédiés',
    oneTime: 'unique',
    bestValue: 'MEILLEURE OFFRE',
    saveMore: 'ÉCONOMISEZ 5$',
    bundlePack: 'Pack Bundle',
    forSeriousTeams: 'Pour équipes sérieuses',
    unlockProPack: 'Débloquer Pack Pro',
    unlockBundle: 'Débloquer Bundle',
    proUnlocked: 'Pack Pro Débloqué',
    bundleUnlocked: 'Bundle Débloqué',
    whatsIn: 'Que contient le Pack Pro ?',
    printReady: 'PDFs Prêts à Imprimer',
    printReadyDesc: 'Distribuez les fiches à votre équipe ou apportez-les au terrain',
    offlineAccess: 'Accès Hors-ligne',
    offlineDesc: 'Téléchargez une fois, utilisez partout — pas besoin d\'internet',
    allDrills: '38 Drills',
    allDrillsDesc: 'Tous les exercices de frappe, lancer, défense, course de bases',

    yourFavorites: 'Vos Favoris',
    savedForLater: 'Exercices que vous avez sauvegardés',
    noFavoritesYet: 'Aucun favori pour le moment',
    tapHeart: 'Touchez l\'icône cœur sur un exercice pour le sauvegarder.',
    browseExercises: 'Parcourir les Exercices',

    verifyingPayment: 'Vérification du paiement...',
    pleaseHold: 'Veuillez patienter pendant que nous confirmons votre achat.',
    proPackUnlocked: '🎉 Pack Pro Débloqué !',
    canDownload: 'Vous pouvez maintenant télécharger les fiches PDF des 38 exercices.',
    paymentCancelled: 'Paiement Annulé',
    noWorries: 'Pas de souci ! Vous pouvez utiliser les exercices gratuits.',
    tryAgain: 'Réessayer',
    sessionExpired: 'Session Expirée',
    expiredMsg: 'Votre session a expiré. Veuillez réessayer.',
    downloadAllZip: 'Télécharger Tout en ZIP',

    // Tier names + gating
    basicPlan: 'Basique',
    proPlan: 'Pro',
    ultimatePlan: 'Ultimate',
    basicTagline: '15 exercices essentiels pour commencer',
    proTagline: '25 exercices + PDFs téléchargeables',
    ultimateTagline: 'Les 38 exercices + téléchargement ZIP',
    locked: 'Verrouillé',
    upgradeToUnlock: 'Améliorez pour débloquer',
    upgradeToPro: 'Passer à Pro',
    upgradeToUltimate: 'Passer à Ultimate',
    exerciseLockedTitle: 'Cet exercice est verrouillé',
    exerciseLockedMsg: 'Améliorez votre plan pour débloquer cet exercice et plus.',
    viewPlans: 'Voir les Plans',
    yourCurrentPlan: 'Votre plan actuel',
    save5: 'Économisez 5$',
  },
  es: {
    home: 'Inicio',
    exercises: 'Ejercicios',
    favorites: 'Favoritos',
    proPack: 'Pack Pro',
    backToHome: 'Volver al Inicio',
    language: 'Idioma',

    badge: 'Edad 9-12 • Jugadores de Little League',
    heroLine1: 'Conviértete en un Mejor',
    heroLine2: 'Jugador de Béisbol',
    heroSubtitle: 'Domina las habilidades esenciales del béisbol con ejercicios divertidos diseñados específicamente para jóvenes atletas. ¡Desde batear hasta lanzar, tenemos los drills para ayudarte a destacar!',
    exploreExercises: 'Explorar Ejercicios',
    watchDemo: 'Ver Demo',
    whyOurTraining: '¿Por Qué Funciona Nuestro Entrenamiento?',
    designedByCoaches: 'Diseñado por entrenadores que entienden el béisbol juvenil',
    popularExercises: 'Ejercicios Populares',
    startWithThese: 'Comienza con estos drills favoritos',
    viewExercise: 'Ver Ejercicio',
    viewAllExercises: 'Ver Todos los Ejercicios',
    readyToLevelUp: '¿Listo para subir de nivel?',
    joinThousands: 'Únete a miles de jóvenes jugadores que mejoran sus habilidades todos los días.',
    startTrainingNow: 'Empezar Ahora',

    exerciseLibrary: 'Biblioteca de Ejercicios',
    browseAll: 'Explora nuestra colección completa de ejercicios de béisbol',
    filterExercises: 'Filtrar Ejercicios',
    findPerfect: 'Encuentra el drill perfecto para tu sesión',
    searchPlaceholder: 'Buscar ejercicios por nombre o descripción...',
    skillCategory: 'Categoría de Habilidad',
    difficultyLevel: 'Nivel de Dificultad',
    resetFilters: 'Restablecer',
    showing: 'Mostrando',
    exercise: 'ejercicio',
    exercisesPlural: 'ejercicios',
    loading: 'Cargando ejercicios...',
    noExercises: 'No se encontraron ejercicios',
    tryAdjusting: 'Intenta ajustar tus filtros o búsqueda',
    viewDetails: 'Ver Detalles',
    duration: 'Duración',
    equipment: 'Equipo',

    back: 'Atrás',
    downloadDrillCard: 'Descargar Ficha',
    favorite: 'Favorito',
    favorited: 'En Favoritos',
    videoDemo: 'Demostración en Video',
    stepByStep: 'Instrucciones Paso a Paso',
    done: 'completado',
    proTips: 'Consejos Pro',
    equipmentNeeded: 'Equipo Necesario',
    quickInfo: 'Info Rápida',
    skillFocus: 'Habilidad',
    difficulty: 'Dificultad',
    moreExercises: 'Más',
    exerciseComplete: '🏆 ¡Ejercicio Completado!',
    keepPracticing: '¡Buen trabajo! Sigue practicando para dominar.',
    shopAmazon: '🛒 Comprar en Amazon →',
    affiliateDisclosure: 'Como Amazon Associate, ganamos con compras elegibles.',
    translationFallback: 'Traducción no disponible, mostrando contenido en inglés.',

    pricingTitle: 'Precio',
    pricingTitleEm: 'Único',
    pricingTitleEnd: 'Simple',
    pricingSubtitle: 'Sin suscripción. Paga una vez, es tuyo para siempre.',
    unlockPremium: 'Desbloquear Drills Premium',
    free: 'Gratis',
    perfectStart: 'Perfecto para empezar',
    forever: 'para siempre',
    currentPlan: 'Plan Actual',
    proPackTitle: 'Pack Pro',
    forCoaches: 'Para entrenadores y jugadores dedicados',
    oneTime: 'único',
    bestValue: 'MEJOR OFERTA',
    saveMore: 'AHORRA $5',
    bundlePack: 'Pack Bundle',
    forSeriousTeams: 'Para equipos serios',
    unlockProPack: 'Desbloquear Pack Pro',
    unlockBundle: 'Desbloquear Bundle',
    proUnlocked: 'Pack Pro Desbloqueado',
    bundleUnlocked: 'Bundle Desbloqueado',
    whatsIn: '¿Qué incluye el Pack Pro?',
    printReady: 'PDFs Listos para Imprimir',
    printReadyDesc: 'Reparte las fichas a tu equipo o llévalas al campo',
    offlineAccess: 'Acceso Sin Conexión',
    offlineDesc: 'Descarga una vez, usa donde quieras — sin internet',
    allDrills: '38 Drills',
    allDrillsDesc: 'Todos los ejercicios de bateo, lanzamiento, defensa, correr bases',

    yourFavorites: 'Tus Favoritos',
    savedForLater: 'Ejercicios que has guardado',
    noFavoritesYet: 'Aún no hay favoritos',
    tapHeart: 'Toca el ícono de corazón en cualquier ejercicio para guardarlo.',
    browseExercises: 'Explorar Ejercicios',

    verifyingPayment: 'Verificando pago...',
    pleaseHold: 'Por favor espera mientras confirmamos tu compra.',
    proPackUnlocked: '🎉 ¡Pack Pro Desbloqueado!',
    canDownload: 'Ahora puedes descargar las fichas PDF de los 38 ejercicios.',
    paymentCancelled: 'Pago Cancelado',
    noWorries: '¡No te preocupes! Puedes usar los ejercicios gratuitos.',
    tryAgain: 'Reintentar',
    sessionExpired: 'Sesión Expirada',
    expiredMsg: 'Tu sesión ha expirado. Por favor intenta de nuevo.',
    downloadAllZip: 'Descargar Todo en ZIP',

    // Tier names + gating
    basicPlan: 'Básico',
    proPlan: 'Pro',
    ultimatePlan: 'Ultimate',
    basicTagline: '15 ejercicios esenciales para comenzar',
    proTagline: '25 ejercicios + PDFs descargables',
    ultimateTagline: 'Los 38 ejercicios + descarga ZIP',
    locked: 'Bloqueado',
    upgradeToUnlock: 'Actualiza para desbloquear',
    upgradeToPro: 'Actualizar a Pro',
    upgradeToUltimate: 'Actualizar a Ultimate',
    exerciseLockedTitle: 'Este ejercicio está bloqueado',
    exerciseLockedMsg: 'Actualiza tu plan para desbloquear este ejercicio y más.',
    viewPlans: 'Ver Planes',
    yourCurrentPlan: 'Tu plan actual',
    save5: 'Ahorra $5',
  },
};

const I18nContext = createContext({ lang: 'en', setLang: () => {}, t: (k) => k });

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    const stored = localStorage.getItem('lang') || 'en';
    // Migrate users who previously selected Mandarin (no longer supported)
    return ['en', 'fr', 'es'].includes(stored) ? stored : 'en';
  });
  useEffect(() => { localStorage.setItem('lang', lang); }, [lang]);
  const setLang = (l) => setLangState(l);
  const t = (key) => (translations[lang] && translations[lang][key]) || translations.en[key] || key;
  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);
